import { useMemo, useState, useRef, type MouseEvent } from "react";
import { Eye, EyeOff, Info, Layers, LineChart, Table, TrendingUp, Calendar, ShieldCheck } from "lucide-react";
import type { DashboardPerformance } from "./types.ts";
import {
  getLieflatNiceScale,
  getSynchronizedRateScale,
  getLieflatSmoothPath,
  getLieflatSmoothAreaPath,
  formatLieflatCurrencyShort,
  formatLieflatRate,
  LIEFLAT_TOKENS,
} from "./lieflatTokens.ts";

function LegendLineIndicator({ type }: { type: "sales" | "spend" | "acos" | "tacos" }) {
  if (type === "sales") {
    return (
      <svg className="legend-line-svg" width="22" height="12" viewBox="0 0 22 12" aria-hidden="true">
        <line x1="0" y1="6" x2="22" y2="6" stroke="#0d9488" strokeWidth="2.5" />
        <circle cx="11" cy="6" r="3.5" fill="#0d9488" />
      </svg>
    );
  }
  if (type === "spend") {
    return (
      <svg className="legend-line-svg" width="22" height="12" viewBox="0 0 22 12" aria-hidden="true">
        <line x1="0" y1="6" x2="22" y2="6" stroke="#0284c7" strokeWidth="2.5" />
        <circle cx="11" cy="6" r="3.5" fill="#0284c7" />
      </svg>
    );
  }
  if (type === "acos") {
    return (
      <svg className="legend-line-svg" width="22" height="12" viewBox="0 0 22 12" aria-hidden="true">
        <line x1="0" y1="6" x2="22" y2="6" stroke="#d97706" strokeWidth="2" strokeDasharray="3 2" />
        <circle cx="11" cy="6" r="3" fill="var(--canvas)" stroke="#d97706" strokeWidth="2" />
      </svg>
    );
  }
  return (
    <svg className="legend-line-svg" width="22" height="12" viewBox="0 0 22 12" aria-hidden="true">
      <line x1="0" y1="6" x2="22" y2="6" stroke="#9333ea" strokeWidth="1.8" strokeDasharray="2 2" />
      <circle cx="11" cy="6" r="2.5" fill="var(--canvas)" stroke="#9333ea" strokeWidth="1.5" />
    </svg>
  );
}

export interface PerformanceTrendChartProps {
  performance: DashboardPerformance | null;
  currency: string;
  loading?: boolean;
}

export function PerformanceTrendChart({
  performance,
  currency,
  loading = false,
}: PerformanceTrendChartProps) {
  const [visibleSeries, setVisibleSeries] = useState({
    sales: true,
    spend: false,
    acos: true,
    tacos: false,
  });
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<"combo" | "chart" | "table">("combo");
  const svgRef = useRef<SVGSVGElement>(null);

  const series = useMemo(() => performance?.series ?? [], [performance]);
  const salesLabel = performance?.ad_sales_vat_basis === "VAT_INCLUSIVE" ? "广告销售额（含税）" : "广告销售额（未税）";

  const toggleSeries = (key: keyof typeof visibleSeries) => {
    setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // SVG Chart Layout Dimensions
  const svgWidth = 920;
  const svgHeight = 290;
  const padLeft = 65;
  const padRight = 60;
  const padTop = 24;
  const padBottom = 35;
  const plotWidth = svgWidth - padLeft - padRight;
  const plotHeight = svgHeight - padTop - padBottom;

  // Max calculations from ONLY visible curves
  const rawMaxAmount = useMemo(() => {
    if (!series.length) return 100;
    const values: number[] = [];
    for (const s of series) {
      if (visibleSeries.sales) values.push(s.ad_sales);
      if (visibleSeries.spend) values.push(s.ad_spend);
    }
    if (!values.length) {
      return Math.max(...series.map((s) => s.ad_sales), 10);
    }
    return Math.max(...values, 10);
  }, [series, visibleSeries.sales, visibleSeries.spend]);

  const rawMaxRate = useMemo(() => {
    if (!series.length) return 0.4;
    const values: number[] = [];
    for (const s of series) {
      if (visibleSeries.acos && s.acos != null) values.push(s.acos);
      if (visibleSeries.tacos && s.tacos != null) values.push(s.tacos);
    }
    if (!values.length) {
      return Math.max(...series.map((s) => s.acos ?? 0), 0.3);
    }
    return Math.max(...values, 0.1);
  }, [series, visibleSeries.acos, visibleSeries.tacos]);

  const amountScale = useMemo(
    () => getLieflatNiceScale(rawMaxAmount, 4, { isCurrency: true }),
    [rawMaxAmount]
  );
  const intervalCount = Math.max(1, amountScale.ticks.length - 1);
  const rateScale = useMemo(
    () => getSynchronizedRateScale(rawMaxRate, intervalCount),
    [rawMaxRate, intervalCount]
  );

  const maxAmount = amountScale.maxAxis;
  const maxRate = rateScale.maxAxis;

  const getX = (index: number) => {
    if (series.length <= 1) return padLeft + plotWidth / 2;
    return padLeft + (index / (series.length - 1)) * plotWidth;
  };

  const getYAmount = (val: number) => {
    return padTop + plotHeight - (Math.max(0, val) / maxAmount) * plotHeight;
  };

  const getYRate = (val: number) => {
    return padTop + plotHeight - (Math.max(0, val) / maxRate) * plotHeight;
  };

  // Smooth Bezier Curve paths
  const salesPoints = useMemo(
    () => series.map((s, i) => ({ x: getX(i), y: getYAmount(s.ad_sales) })),
    [series, maxAmount]
  );

  const spendPoints = useMemo(
    () => series.map((s, i) => ({ x: getX(i), y: getYAmount(s.ad_spend) })),
    [series, maxAmount]
  );

  const acosPoints = useMemo(
    () =>
      series
        .map((s, i) => (s.acos == null ? null : { x: getX(i), y: getYRate(s.acos) }))
        .filter((p): p is { x: number; y: number } => p !== null),
    [series, maxRate]
  );

  const tacosPoints = useMemo(
    () =>
      series
        .map((s, i) => (s.tacos == null ? null : { x: getX(i), y: getYRate(s.tacos) }))
        .filter((p): p is { x: number; y: number } => p !== null),
    [series, maxRate]
  );

  const salesLinePath = useMemo(
    () => (visibleSeries.sales ? getLieflatSmoothPath(salesPoints) : ""),
    [salesPoints, visibleSeries.sales]
  );

  const salesAreaPath = useMemo(
    () => (visibleSeries.sales ? getLieflatSmoothAreaPath(salesPoints, padTop + plotHeight) : ""),
    [salesPoints, visibleSeries.sales]
  );

  const spendLinePath = useMemo(
    () => (visibleSeries.spend ? getLieflatSmoothPath(spendPoints) : ""),
    [spendPoints, visibleSeries.spend]
  );

  const spendAreaPath = useMemo(
    () => (visibleSeries.spend ? getLieflatSmoothAreaPath(spendPoints, padTop + plotHeight) : ""),
    [spendPoints, visibleSeries.spend]
  );

  const acosLinePath = useMemo(
    () => (visibleSeries.acos ? getLieflatSmoothPath(acosPoints) : ""),
    [acosPoints, visibleSeries.acos]
  );

  const tacosLinePath = useMemo(
    () => (visibleSeries.tacos ? getLieflatSmoothPath(tacosPoints) : ""),
    [tacosPoints, visibleSeries.tacos]
  );

  // X Axis Label selection (spread 5-7 evenly)
  const xLabels = useMemo(() => {
    if (!series.length) return [];
    if (series.length <= 7) return series.map((s, i) => ({ index: i, date: s.date.slice(5) }));
    const step = Math.ceil((series.length - 1) / 6);
    const indices: number[] = [];
    for (let i = 0; i < series.length; i += step) indices.push(i);
    if (indices[indices.length - 1] !== series.length - 1) indices.push(series.length - 1);
    return indices.map((i) => ({ index: i, date: series[i].date.slice(5) }));
  }, [series]);

  // Mouse move handler for Tooltip
  const handleMouseMove = (e: MouseEvent<SVGSVGElement>) => {
    if (!series.length || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const svgX = (clientX / rect.width) * svgWidth;
    if (svgX < padLeft || svgX > padLeft + plotWidth) {
      setHoverIndex(null);
      return;
    }
    const ratio = (svgX - padLeft) / plotWidth;
    const index = Math.round(ratio * (series.length - 1));
    if (index >= 0 && index < series.length) {
      setHoverIndex(index);
    }
  };

  const hoveredItem = hoverIndex !== null && hoverIndex >= 0 && hoverIndex < series.length ? series[hoverIndex] : null;

  // Editorial conclusion summary for title
  const totalSpend = performance?.totals?.ad_spend ?? 0;
  const totalSales = performance?.totals?.ad_sales ?? 0;
  const overallAcos = performance?.totals?.acos ?? 0;
  const isHealthyAcos = overallAcos > 0 && overallAcos <= 0.28;

  return (
    <div className="trend-chart-container lieflat-card">
      {/* 1. Lieflat Header: Conclusion-First Title + Editorial Subtitle */}
      <div className="lieflat-header">
        <h2 className="lieflat-title">
          {totalSales > 0
            ? `广告产出与投产比走势 · ${isHealthyAcos ? "投产效率处于健康区间" : "持续推进投放收敛与效率优化"}`
            : "广告投放与销售转化趋势大盘"}
        </h2>
        <div className="lieflat-sub">
          <span>{salesLabel} vs 广告花费</span>
          <span className="sep">·</span>
          <span>双轴 ACOS / TACOS 效率比对</span>
          <span className="sep">·</span>
          <span>{performance?.window_days ?? 7} 日归因观测周期</span>
        </div>
      </div>

      {/* Chart Toolbar / Controls */}
      <div className="trend-chart-toolbar">
        <div className="legend-pills" role="toolbar" aria-label="图表指标开关">
          <button
            type="button"
            className={`legend-pill pill-sales ${visibleSeries.sales ? "active" : "muted"}`}
            onClick={() => toggleSeries("sales")}
            title="切换广告销售额曲线展示"
          >
            <LegendLineIndicator type="sales" />
            <span>{salesLabel}</span>
            {visibleSeries.sales ? <Eye size={12} /> : <EyeOff size={12} />}
          </button>

          <button
            type="button"
            className={`legend-pill pill-spend ${visibleSeries.spend ? "active" : "muted"}`}
            onClick={() => toggleSeries("spend")}
            title="切换广告花费曲线展示"
          >
            <LegendLineIndicator type="spend" />
            <span>广告花费</span>
            {visibleSeries.spend ? <Eye size={12} /> : <EyeOff size={12} />}
          </button>

          <button
            type="button"
            className={`legend-pill pill-acos ${visibleSeries.acos ? "active" : "muted"}`}
            onClick={() => toggleSeries("acos")}
            title="切换 ACOS 曲线展示"
          >
            <LegendLineIndicator type="acos" />
            <span>ACOS</span>
            {visibleSeries.acos ? <Eye size={12} /> : <EyeOff size={12} />}
          </button>

          <button
            type="button"
            className={`legend-pill pill-tacos ${visibleSeries.tacos ? "active" : "muted"}`}
            onClick={() => toggleSeries("tacos")}
            title="切换 TACOS 曲线展示"
          >
            <LegendLineIndicator type="tacos" />
            <span>TACOS</span>
            {visibleSeries.tacos ? <Eye size={12} /> : <EyeOff size={12} />}
          </button>
        </div>

        <div className="view-mode-toggle" role="group" aria-label="图表视图切换">
          <button
            type="button"
            className={viewMode === "combo" ? "active" : ""}
            onClick={() => setViewMode("combo")}
            title="组合视图 (图表 + 每日表格)"
          >
            <Layers size={14} />
            <span>组合视图</span>
          </button>
          <button
            type="button"
            className={viewMode === "chart" ? "active" : ""}
            onClick={() => setViewMode("chart")}
            title="仅展示走势图表"
          >
            <LineChart size={14} />
            <span>走势图</span>
          </button>
          <button
            type="button"
            className={viewMode === "table" ? "active" : ""}
            onClick={() => setViewMode("table")}
            title="仅展示数据表格"
          >
            <Table size={14} />
            <span>明细表</span>
          </button>
        </div>
      </div>

      {/* 2. SVG Chart Visualization (Lieflat Hairline & Bezier) */}
      {viewMode !== "table" && (
        <div className="trend-svg-wrapper">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className="trend-svg"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <defs>
              {/* Sales Gradient - Atmospheric Teal / Porcelain tint */}
              <linearGradient id="salesLieflatGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0d9488" stopOpacity="0.18" />
                <stop offset="90%" stopColor="#0d9488" stopOpacity="0.02" />
                <stop offset="100%" stopColor="#0d9488" stopOpacity="0" />
              </linearGradient>
              {/* Spend Gradient - Atmospheric Blue / Wire tint */}
              <linearGradient id="spendLieflatGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0284c7" stopOpacity="0.16" />
                <stop offset="90%" stopColor="#0284c7" stopOpacity="0.02" />
                <stop offset="100%" stopColor="#0284c7" stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* 1. Grid & Axis Lines (Aligned to Nice Number intervals) */}
            {amountScale.ticks.map((amountVal, index) => {
              const stepRatio = index / intervalCount;
              const y = padTop + plotHeight - stepRatio * plotHeight;
              const rateVal = rateScale.ticks[index] ?? 0;
              return (
                <g key={index} className="grid-row">
                  <line
                    x1={padLeft}
                    y1={y}
                    x2={padLeft + plotWidth}
                    y2={y}
                    stroke="var(--line)"
                    strokeWidth={index === 0 ? "1" : "0.8"}
                    strokeDasharray={index === 0 ? "none" : LIEFLAT_TOKENS.stroke.dashGuide}
                  />
                  {/* Left Tick Label (Currency Nice Number) */}
                  <text
                    x={padLeft - 10}
                    y={y + 4}
                    textAnchor="end"
                    className="axis-label axis-amount"
                    style={{ fontSize: "11px", fontWeight: LIEFLAT_TOKENS.typography.axisWeight }}
                  >
                    {formatLieflatCurrencyShort(amountVal, currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$")}
                  </text>
                  {/* Right Tick Label (Percentage Nice Number) */}
                  <text
                    x={padLeft + plotWidth + 10}
                    y={y + 4}
                    textAnchor="start"
                    className="axis-label axis-rate"
                    style={{ fontSize: "11px", fontWeight: LIEFLAT_TOKENS.typography.axisWeight }}
                  >
                    {formatLieflatRate(rateVal)}
                  </text>
                </g>
              );
            })}

            {/* 2. X-Axis Date Labels */}
            {xLabels.map((item) => {
              const x = getX(item.index);
              return (
                <text
                  key={item.index}
                  x={x}
                  y={padTop + plotHeight + 20}
                  textAnchor="middle"
                  className="axis-label axis-date"
                  style={{ fontSize: "11.5px", fontWeight: 600 }}
                >
                  {item.date}
                </text>
              );
            })}

            {/* 3. Area Fills (Layered underneath lines) */}
            {visibleSeries.sales && salesAreaPath && (
              <path d={salesAreaPath} fill="url(#salesLieflatGradient)" />
            )}
            {visibleSeries.spend && spendAreaPath && (
              <path d={spendAreaPath} fill="url(#spendLieflatGradient)" />
            )}

            {/* 4. Stroke Lines (Layered with crisp contrast) */}
            {visibleSeries.sales && salesLinePath && (
              <path
                d={salesLinePath}
                fill="none"
                stroke="#0d9488"
                strokeWidth={LIEFLAT_TOKENS.stroke.hero}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {visibleSeries.spend && spendLinePath && (
              <path
                d={spendLinePath}
                fill="none"
                stroke="#0284c7"
                strokeWidth={LIEFLAT_TOKENS.stroke.curve}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {visibleSeries.tacos && tacosLinePath && (
              <path
                d={tacosLinePath}
                fill="none"
                stroke="#9333ea"
                strokeWidth={LIEFLAT_TOKENS.stroke.curve}
                strokeDasharray="3 3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {visibleSeries.acos && acosLinePath && (
              <path
                d={acosLinePath}
                fill="none"
                stroke="#d97706"
                strokeWidth={LIEFLAT_TOKENS.stroke.curve}
                strokeDasharray="5 3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* 5. Crosshair & Dynamic Focus Points with Halo on Hover */}
            {hoverIndex !== null && hoverIndex >= 0 && hoverIndex < series.length && (
              <g className="crosshair-group">
                <line
                  x1={getX(hoverIndex)}
                  y1={padTop}
                  x2={getX(hoverIndex)}
                  y2={padTop + plotHeight}
                  stroke="var(--line-strong)"
                  strokeWidth="1.2"
                  strokeDasharray="3 3"
                />

                {/* Highlight active points with crisp halo ring */}
                {visibleSeries.sales && (
                  <circle
                    cx={getX(hoverIndex)}
                    cy={getYAmount(series[hoverIndex].ad_sales)}
                    r="5"
                    fill="#0d9488"
                    stroke="var(--canvas)"
                    strokeWidth="3"
                  />
                )}
                {visibleSeries.spend && (
                  <circle
                    cx={getX(hoverIndex)}
                    cy={getYAmount(series[hoverIndex].ad_spend)}
                    r="5"
                    fill="#0284c7"
                    stroke="var(--canvas)"
                    strokeWidth="3"
                  />
                )}
                {visibleSeries.acos && series[hoverIndex].acos != null && (
                  <circle
                    cx={getX(hoverIndex)}
                    cy={getYRate(series[hoverIndex].acos!)}
                    r="5"
                    fill="#d97706"
                    stroke="var(--canvas)"
                    strokeWidth="3"
                  />
                )}
                {visibleSeries.tacos && series[hoverIndex].tacos != null && (
                  <circle
                    cx={getX(hoverIndex)}
                    cy={getYRate(series[hoverIndex].tacos!)}
                    r="5"
                    fill="#9333ea"
                    stroke="var(--canvas)"
                    strokeWidth="3"
                  />
                )}
              </g>
            )}
          </svg>

          {/* Interactive Floating Tooltip */}
          {hoveredItem && hoverIndex !== null && (
            <div
              className="trend-tooltip lieflat-tooltip"
              style={{
                left: `${Math.min(
                  Math.max(12, (getX(hoverIndex) / svgWidth) * 100),
                  82
                )}%`,
              }}
            >
              <div className="tooltip-header">
                <strong>{hoveredItem.date}</strong>
                <span>{getDayOfWeek(hoveredItem.date)}</span>
              </div>
              <div className="tooltip-grid">
                {visibleSeries.sales && (
                  <div className="tooltip-row sales-row">
                    <span className="dot dot-sales" />
                    <span className="label">{salesLabel}:</span>
                    <strong className="value">{formatMoney(hoveredItem.ad_sales, currency)}</strong>
                  </div>
                )}
                {visibleSeries.spend && (
                  <div className="tooltip-row spend-row">
                    <span className="dot dot-spend" />
                    <span className="label">广告花费:</span>
                    <strong className="value">{formatMoney(hoveredItem.ad_spend, currency)}</strong>
                  </div>
                )}
                {visibleSeries.acos && (
                  <div className="tooltip-row acos-row">
                    <span className="dot dot-acos" />
                    <span className="label">ACOS:</span>
                    <strong className="value">
                      {hoveredItem.acos != null ? `${(hoveredItem.acos * 100).toFixed(2)}%` : "-"}
                    </strong>
                  </div>
                )}
                {visibleSeries.tacos && (
                  <div className="tooltip-row tacos-row">
                    <span className="dot dot-tacos" />
                    <span className="label">TACOS:</span>
                    <strong className="value">
                      {hoveredItem.tacos != null ? `${(hoveredItem.tacos * 100).toFixed(2)}%` : "-"}
                    </strong>
                  </div>
                )}
              </div>
              <div className="tooltip-footer">
                <span>订单: <b>{hoveredItem.ad_orders}</b></span>
                <span>点击: <b>{hoveredItem.clicks}</b></span>
                <span>
                  CPC: <b>{hoveredItem.clicks > 0 ? formatMoney(hoveredItem.ad_spend / hoveredItem.clicks, currency) : "-"}</b>
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3. Embedded Table Mode / Combo Table */}
      {viewMode !== "chart" && (
        <div className="trend-table-wrapper">
          <table className="table-zebra">
            <thead>
              <tr>
                <th className="date-col">日期</th>
                <th className="num-col">广告花费</th>
                <th className="num-col">{salesLabel}</th>
                <th className="num-col">订单量</th>
                <th className="num-col">点击数</th>
                <th className="num-col" title={`ACOS = 广告花费 ÷ ${salesLabel} (基准: ≤ 25% 优秀，≤ 35% 正常)`}>
                  <span className="th-with-info">
                    <span>ACOS</span>
                    <Info size={11} className="th-info-icon" />
                  </span>
                </th>
                <th className="num-col" title="TACOS = 广告花费 ÷ 店铺总销售额">
                  <span className="th-with-info">
                    <span>TACOS</span>
                    <Info size={11} className="th-info-icon" />
                  </span>
                </th>
                <th className="trend-col">花费趋势</th>
              </tr>
            </thead>
            <tbody>
              {series.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-cell">
                    {loading ? "正在加载每日表现明细..." : "当前周期暂无明细数据"}
                  </td>
                </tr>
              ) : (
                series
                  .slice()
                  .reverse()
                  .map((row) => {
                    const spendRatio = maxAmount > 0 ? (row.ad_spend / maxAmount) * 100 : 0;
                    return (
                      <tr key={row.date}>
                        <td className="date-cell">
                          <strong>{row.date}</strong>
                          <small>{getDayOfWeek(row.date)}</small>
                        </td>
                        <td className="num-col font-mono">{formatMoney(row.ad_spend, currency)}</td>
                        <td className="num-col font-mono">{formatMoney(row.ad_sales, currency)}</td>
                        <td className="num-col font-mono">{row.ad_orders}</td>
                        <td className="num-col font-mono">{row.clicks}</td>
                        <td className="num-col font-mono">
                          {row.acos != null ? (
                            <span
                              className={`rate-cell ${
                                row.acos <= 0.25
                                  ? "rate-good"
                                  : row.acos <= 0.35
                                  ? "rate-normal"
                                  : "rate-high"
                              }`}
                              title={`ACOS: ${(row.acos * 100).toFixed(2)}% · ${
                                row.acos <= 0.25
                                  ? "表现优良 (≤ 25%)"
                                  : row.acos <= 0.35
                                  ? "处于健康区间"
                                  : "偏高 (> 35%)"
                              }`}
                            >
                              {(row.acos * 100).toFixed(2)}%
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="num-col font-mono">
                          {row.tacos != null ? `${(row.tacos * 100).toFixed(2)}%` : "-"}
                        </td>
                        <td className="trend-col">
                          <div className="inline-bar-track" title={`花费占比 ${spendRatio.toFixed(1)}%`}>
                            <div
                              className="inline-bar-fill"
                              style={{ width: `${Math.min(100, Math.max(4, spendRatio))}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* 4. Lieflat Footer: Source & Attribution Methodology */}
      <div className="lieflat-src">
        <div className="src-meta">
          <span className="src-badge">SP · SB · SD</span>
          <span>亚马逊广告全量归因口径（14-Day Attribution）</span>
        </div>
        <div>
          <span>更新时间: {performance?.updated_at ? new Date(performance.updated_at).toLocaleDateString() : "今日"}</span>
        </div>
      </div>
    </div>
  );
}

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
}

function getDayOfWeek(dateStr: string): string {
  const day = new Date(dateStr).getDay();
  return ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][day] ?? "";
}
